import { useLingui } from "@lingui/react/macro";
import { GripHorizontal, X } from "lucide-react";
import React, {
  createContext,
  Suspense,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@/utils/shadcn-ui";

import {
  Dialog as BaseDialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "./base/dialog";
import { Button } from "./button";
import { useDockedPosition } from "./docked-position";
import { Spinner } from "./spinner";

export function Dialog({
  children,
  trigger,
  open,
  onOpenChange,
  className,
  width,
  docked,
  attachToParent,
  dismissible = true,
}: {
  children?: React.ReactNode;
  /** The control that opens the dialog, wrapped in `DialogTrigger`; it stays
      where it is placed, and focus returns to it on close. Without `open` the
      dialog keeps its own open state, which suits a read-only dialog: a close
      guard needs `open` and `onOpenChange` to hold the dialog open. */
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** Desktop width, e.g. `"620px"`. Below `sm` the dialog is full-bleed
      regardless of this. */
  width?: string;
  docked?: boolean;
  /** Docked only: open beside the dialog this one is rendered in, rather than
      at the screen's right edge. */
  attachToParent?: boolean;
  /** Off for a dialog showing something that can't be shown again: only its
      Close and its footer close it, never a stray Escape or click outside. */
  dismissible?: boolean;
}) {
  const parent = useContext(DialogElementContext);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const dock = useDockedPosition(
    docked ? content : null,
    attachToParent ? parent : null,
  );
  const { position } = dock;
  const guard = useRef<CloseGuard | null>(null);
  // Memoized: a new value would re-render every guarded form on each pointer
  // move while the dialog is dragged.
  const close = useMemo(() => {
    async function closeIfAllowed() {
      if (guard.current && !(await guard.current())) return;
      onOpenChange?.(false);
    }
    // Returns nothing, so a Cancel button does not show itself pending while
    // the guard's question is up.
    return { guard, requestClose: () => void closeIfAllowed() };
  }, [onOpenChange]);

  return (
    <BaseDialog
      open={open}
      onOpenChange={(next) =>
        next ? onOpenChange?.(true) : close.requestClose()
      }
      modal={!docked}
    >
      {trigger}
      <DialogContent
        ref={setContent}
        {...(docked ? dock.contentProps : undefined)}
        // Both halves are needed: `w-` sets the width, and `sm:max-w-` lifts
        // DialogContent's own `sm:max-w-lg` (512px), which would otherwise cap
        // anything wider. The value rides in a CSS var rather than an inline
        // `width`, which would outrank the `max-sm:` full-bleed rule.
        className={cn(
          DIALOG_CARD_CLASS,
          width && "w-(--dialog-width) sm:max-w-(--dialog-width)",
          docked && [
            DOCKED_CLASS,
            position
              ? "sm:top-(--dock-y) sm:left-(--dock-x)"
              : "sm:top-[5dvh] sm:right-4 sm:left-auto",
            dock.dragging && "sm:cursor-grabbing",
          ],
          className,
        )}
        style={
          {
            ...(width ? { "--dialog-width": width } : undefined),
            ...(position
              ? { "--dock-x": `${position.x}px`, "--dock-y": `${position.y}px` }
              : undefined),
          } as React.CSSProperties
        }
        showCloseButton={false}
        onInteractOutside={
          docked || !dismissible ? (e) => e.preventDefault() : undefined
        }
        onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          const start = content && firstControl(content);
          if (!start) return;
          e.preventDefault();
          start.focus();
        }}
      >
        <DialogElementContext.Provider value={content}>
          <CloseGuardContext.Provider value={close}>
            <DockGripContext.Provider value={docked ? dock.onKeyDown : null}>
              <Suspense
                fallback={
                  <div className="flex min-h-32 items-center justify-center">
                    <Spinner />
                  </div>
                }
              >
                {children}
              </Suspense>
            </DockGripContext.Provider>
          </CloseGuardContext.Provider>
        </DialogElementContext.Provider>
      </DialogContent>
    </BaseDialog>
  );
}

/**
 * The dialog card: its sections — DialogHeader, DialogBody, DialogFooter —
 * bring their own padding, so the hairlines and the footer strip run edge to
 * edge. `overflow-hidden` clips the strip to the card's corners.
 */
export { DialogTrigger };

export const DIALOG_CARD_CLASS = "gap-0 overflow-hidden p-0 max-sm:p-0";

/**
 * The title row: the title in `text-lg` semibold, an optional description
 * under it, and the dialog's own icon actions in its right corner — `actions`,
 * then a docked dialog's grip, then Close at the very end — with a hairline
 * below. Close goes the same way as Escape and a click outside, so a dialog's
 * close guard still asks before discarding input.
 */
export function DialogHeader({
  children,
  description,
  closeLabel,
  showClose = true,
  actions,
}: {
  children: React.ReactNode;
  description?: React.ReactNode;
  /** Status and icon-only actions about the whole dialog, placed before the
      grip and Close. */
  actions?: React.ReactNode;
  /** Close's accessible name, when "Close" alone would be ambiguous. */
  closeLabel?: string;
  /** Off only where the user must answer in the footer — `confirm()` and
      `alert()`. */
  showClose?: boolean;
}) {
  const { t } = useLingui();
  const dockGrip = useContext(DockGripContext);
  return (
    <div className="flex shrink-0 items-start gap-3 border-b border-hairline px-6 py-4.5 max-sm:px-4">
      <div className="min-w-0 flex-1">
        <DialogTitle className="text-lg leading-7 font-semibold">
          {children}
        </DialogTitle>
        {description && (
          <DialogDescription className="mt-0.5">
            {description}
          </DialogDescription>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {actions}
        {dockGrip && <DockGrip onKeyDown={dockGrip} />}
        {showClose && (
          // Radix's Close, not the close guard's context: that context reaches
          // through to a dialog opened inside this one on the base Dialog (a
          // ChoosingModal), whose Close would then shut the outer dialog. The
          // guard still runs - `Dialog` routes Radix's close through it.
          <DialogClose asChild>
            <Button
              variant="tertiary"
              size="sm"
              icon={X}
              label={closeLabel ?? t`Close`}
              tooltip={null}
            />
          </DialogClose>
        )}
      </div>
    </div>
  );
}

/**
 * Where focus lands when a dialog opens: the body's first control, else the
 * footer's. Not the header's — its corner holds Close and icon actions whose
 * tooltip opens on focus and would then take the first Escape. A field with
 * `autoFocus` already has focus by then, and Radix leaves it there.
 */
function firstControl(card: HTMLElement): HTMLElement | undefined {
  const selector = ["dialog-body", "dialog-footer"]
    .map((slot) => `[data-slot="${slot}"] :is(${FOCUSABLE})`)
    .join(", ");
  return [...card.querySelectorAll<HTMLElement>(selector)].find(
    (el) => el.getClientRects().length > 0,
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

/** The part of a dialog that scrolls, so the title and the actions stay put. */
export function DialogBody({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="dialog-body"
      className={cn(
        "min-h-0 flex-1 overflow-y-auto px-6 py-5 max-sm:px-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The actions, on a `muted` strip: right-aligned, Cancel first and the primary
 * last. On a phone they stack with the primary on top.
 */
export function DialogFooter({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 flex-col-reverse gap-2 border-t border-hairline bg-muted px-6 py-3.5 max-sm:px-4 sm:flex-row sm:items-center sm:justify-end",
        className,
      )}
    >
      {children}
    </div>
  );
}

type CloseGuard = () => boolean | Promise<boolean>;

/** The card of the enclosing dialog, for a nested one to attach to. */
const DialogElementContext = createContext<HTMLElement | null>(null);

/** A docked dialog's grip handler, for its header to place the grip. */
const DockGripContext = createContext<
  ((e: React.KeyboardEvent<HTMLElement>) => void) | null
>(null);

const CloseGuardContext = createContext<{
  guard: React.RefObject<CloseGuard | null>;
  requestClose: () => void;
} | null>(null);

export function useDialogClose(guard?: CloseGuard): () => void {
  const context = useContext(CloseGuardContext);
  if (!context) throw new Error("useDialogClose must be used inside a Dialog");
  useLayoutEffect(() => {
    if (!guard) return;
    context.guard.current = guard;
    return () => {
      context.guard.current = null;
    };
  });
  return context.requestClose;
}

// Pinned near the top, so a form that changes height leaves the row just
// clicked under the cursor. `transition-none`: DialogContent's `duration-200`
// would otherwise ease left/top, and the card would trail the pointer.
const DOCKED_CLASS =
  "transition-none sm:translate-x-0 sm:translate-y-0 sm:[&_[data-slot=dialog-title]]:cursor-grab sm:[&_[data-slot=dialog-title]]:select-none";

function DockGrip({
  onKeyDown,
}: {
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}) {
  const { t } = useLingui();
  return (
    <Button
      variant="tertiary"
      size="sm"
      icon={GripHorizontal}
      label={t`Move`}
      tooltip={t`Drag to move, or use the arrow keys`}
      aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
      data-dock-handle
      onKeyDown={onKeyDown}
      className="cursor-grab touch-none select-none max-sm:hidden"
    />
  );
}
